import React, { useCallback, useRef, useState, useEffect, createContext, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Command, CommandGroup, CommandInput, CommandItem } from '@/shadcn/components/ui/command';
import { Checkbox } from '@/shadcn/components/ui/checkbox';

// Context for command select state
interface CommandSelectContextValue {
  open: boolean;
  setOpen: (open: boolean | ((prevOpen: boolean) => boolean)) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isUpdating: boolean;
  setIsUpdating: (updating: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  handleClose: () => void;
}

// Hook to access search query from parent components
export const useCommandSelectSearch = () => {
  const context = useContext(CommandSelectContext);
  if (!context) {
    throw new Error('useCommandSelectSearch must be used within CommandSelect');
  }
  return context.searchQuery;
};

const CommandSelectContext = createContext<CommandSelectContextValue | null>(null);

const useCommandSelect = () => {
  const context = useContext(CommandSelectContext);
  if (!context) {
    throw new Error('Command select components must be used within CommandSelect');
  }
  return context;
};

// Root command select component
export interface CommandSelectProps {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}

export const CommandSelect: React.FC<CommandSelectProps> = ({ children, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchQuery(''); // Reset search when closing dropdown
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleSetOpen = useCallback((newOpen: boolean | ((prevOpen: boolean) => boolean)) => {
    setOpen((prevOpen) => {
      const nextOpen = typeof newOpen === 'function' ? newOpen(prevOpen) : newOpen;
      onOpenChange?.(nextOpen);
      if (!nextOpen) {
        setSearchQuery('');
      }
      return nextOpen;
    });
  }, [onOpenChange]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, handleClose]);

  // Cleanup debounce timer
  useEffect(() => {
    const timer = debounceTimerRef.current;
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const contextValue: CommandSelectContextValue = {
    open,
    setOpen: handleSetOpen,
    searchQuery,
    setSearchQuery,
    isUpdating,
    setIsUpdating,
    containerRef,
    handleClose,
  };

  return (
    <CommandSelectContext.Provider value={contextValue}>
      <div ref={containerRef} className="relative">
        {children}
      </div>
    </CommandSelectContext.Provider>
  );
};

// Trigger component
export interface CommandSelectTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
}

export const CommandSelectTrigger: React.FC<CommandSelectTriggerProps> = ({
  children,
  placeholder = 'Select...',
  className = '',
  disabled = false,
  ...props
}) => {
  const { open, setOpen } = useCommandSelect();

  return (
    <div
      onClick={() => {
        if (!disabled) {
          setOpen((prevOpen) => !prevOpen);
        }
      }}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          setOpen((prevOpen) => !prevOpen);
        }
      }}
      className={`w-full flex items-center justify-between gap-2 px-3 text-sm border rounded-md hover:bg-accent hover:border-accent-foreground cursor-pointer h-8 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-disabled={disabled}
      {...props}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {children || (
          <span className="truncate px-3 rounded-md text-xs h-8 flex items-center">{placeholder}</span>
        )}
      </div>
      <FontAwesomeIcon
        icon={faChevronDown}
        className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      />
    </div>
  );
};

// Value display component for selected items
export interface CommandSelectValueProps extends React.HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
  displayText?: string;
  count?: number | null;
  onClear?: (e: React.SyntheticEvent) => void;
  showClear?: boolean;
  disabled?: boolean;
  'data-dd-action-name'?: string;
}

export const CommandSelectValue: React.FC<CommandSelectValueProps> = ({
  placeholder = 'Select...',
  displayText,
  count,
  onClear,
  showClear = true,
  disabled = false,
  'data-dd-action-name': dataDdActionName,
  ...props
}) => {
  const { isUpdating } = useCommandSelect();

  const handleClear = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClear?.(e);
    },
    [onClear],
  );

  if (!displayText) {
    return (
      <span className="truncate px-3 rounded-md text-xs h-8 flex items-center">{placeholder}</span>
    );
  }

  return (
    <div className="flex items-center bg-[#E5E8EB] rounded-md px-3 max-w-full h-6" {...props}>
      <span className="truncate text-xs">{displayText}</span>
      {count && count > 0 && (
        <span className="flex-shrink-0 ml-1 text-xs">+{count}</span>
      )}
      {showClear && onClear && (
        <button
          type="button"
          onMouseDown={handleClear}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              handleClear(e);
            }
          }}
          className="rounded-sm p-0.5 flex-shrink-0 ml-1"
          aria-label="Clear selection"
          disabled={disabled || isUpdating}
          data-dd-action-name={dataDdActionName}
        >
          <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

// Content component
export interface CommandSelectContentProps {
  children: React.ReactNode;
  searchPlaceholder?: string;
  className?: string;
  maxHeight?: string;
  useWrapper?: boolean; // Whether to wrap children in CommandGroup
  onSearchChange?: (search: string) => void; // External search callback
  useExternalSearch?: boolean; // Use external search instead of internal filtering
  showSearch?: boolean; // Whether to show the search input
}

export const CommandSelectContent: React.FC<CommandSelectContentProps> = ({
  children,
  searchPlaceholder = 'Search...',
  className = '',
  maxHeight = '200px',
  useWrapper = true,
  onSearchChange,
  useExternalSearch = false,
  showSearch = true
}) => {
  const { open, setSearchQuery } = useCommandSelect();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search handler for internal search
  const handleInternalSearchChange = useCallback((search: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Immediately set empty value without debounce to reset results
    if (!search || search.length === 0) {
      setSearchQuery('');
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(search);
    }, 150);
  }, [setSearchQuery]);

  // External search handler - calls the callback immediately
  const handleExternalSearchChange = useCallback((search: string) => {
    onSearchChange?.(search);
    // Also update internal search query for other components that might need it
    setSearchQuery(search);
  }, [onSearchChange, setSearchQuery]);

  // Cleanup debounce timer
  useEffect(() => {
    const timer = debounceTimerRef.current;
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  if (!open) return null;

  return (
    <div
      className={`absolute top-full left-0 w-full mt-1 bg-popover border rounded-md shadow-md z-50 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Command shouldFilter={false}>
        {showSearch && (
          <CommandInput
            placeholder={searchPlaceholder}
            className="border-none"
            onValueChange={useExternalSearch ? handleExternalSearchChange : handleInternalSearchChange}
          />
        )}
        {useWrapper ? (
          <CommandGroup className="overflow-auto" style={{ maxHeight }}>
            {children}
          </CommandGroup>
        ) : (
          <div className="overflow-auto" style={{ maxHeight }}>
            {children}
          </div>
        )}
      </Command>
    </div>
  );
};

// Loading state component
export interface CommandSelectLoadingProps {
  children?: React.ReactNode;
  className?: string;
}

export const CommandSelectLoading: React.FC<CommandSelectLoadingProps> = ({
  children = 'Loading...',
  className = ''
}) => {
  return (
    <p className={`p-4 text-sm text-center text-muted-foreground ${className}`}>
      {children}
    </p>
  );
};

// Item component
export interface CommandSelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  value: string;
  onSelect?: () => void;
  disabled?: boolean;
  selected?: boolean;
  showCheckbox?: boolean;
}

export const CommandSelectItem: React.FC<CommandSelectItemProps> = ({
  children,
  value,
  onSelect,
  disabled = false,
  selected = false,
  className = '',
  showCheckbox = true,
  ...props
}) => {
  const { isUpdating } = useCommandSelect();

  return (
    <CommandItem
      value={value}
      onSelect={onSelect}
      className={`flex items-center gap-2 cursor-pointer ${className}`}
      disabled={disabled || isUpdating}
      {...props}
    >
      {showCheckbox && (
        <Checkbox
          checked={selected}
          className="pointer-events-none"
          aria-hidden
        />
      )}
      {children}
    </CommandItem>
  );
};

// Select All component for multi-select scenarios
export interface CommandSelectAllProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onToggle?: () => void;
  count?: number;
  className?: string;
  'data-dd-action-name'?: string;
}

export const CommandSelectAll: React.FC<CommandSelectAllProps> = ({
  checked,
  indeterminate = false,
  disabled = false,
  onToggle,
  count,
  className = '',
  ...props
}) => {
  const userInteractionRef = useRef<boolean>(false);
  const checkboxRef = useRef<HTMLButtonElement>(null);

  // Handle indeterminate state using ref and useEffect
  useEffect(() => {
    if (checkboxRef.current) {
      // Set indeterminate state on the button element
      if (indeterminate) {
        checkboxRef.current.setAttribute('data-state', 'indeterminate');
        checkboxRef.current.setAttribute('aria-checked', 'mixed');
      } else {
        checkboxRef.current.removeAttribute('data-state');
        checkboxRef.current.setAttribute('aria-checked', checked.toString());
      }
    }
  }, [indeterminate, checked]);

  const handleToggle = useCallback(() => {
    // Only proceed if this is from an explicit user interaction
    if (!userInteractionRef.current) {
      return;
    }

    // Reset the user interaction flag
    userInteractionRef.current = false;
    onToggle?.();
  }, [onToggle]);

  return (
    <CommandItem
      onSelect={handleToggle}
      onMouseDown={() => {
        userInteractionRef.current = true;
      }}
      onClick={() => {
        userInteractionRef.current = true;
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          userInteractionRef.current = true;
        }
      }}
      className={`flex items-center gap-2 cursor-pointer border-b bg-muted/50 ${className}`}
      disabled={disabled}
      value=""
      {...props}
    >
      <Checkbox
        ref={checkboxRef}
        checked={checked}
        className="pointer-events-none"
        aria-hidden
      />
      <span
        className="text-sm text-muted-foreground select-none"
        style={{
          opacity: disabled ? 0.5 : 1
        }}
      >
        {checked ? 'Deselect all' : 'Select all'} {typeof count === 'number' && `(${count})`}
      </span>
    </CommandItem>
  );
};

// Empty state component
export interface CommandSelectEmptyProps {
  children: React.ReactNode;
  className?: string;
}

export const CommandSelectEmpty: React.FC<CommandSelectEmptyProps> = ({
  children,
  className = ''
}) => {
  return (
    <p className={`p-4 text-sm text-center text-muted-foreground ${className}`}>
      {children}
    </p>
  );
};

// Group component for organizing items into sections
export interface CommandSelectGroupProps {
  children: React.ReactNode;
  heading?: string;
  className?: string;
}

export const CommandSelectGroup: React.FC<CommandSelectGroupProps> = ({
  children,
  heading,
  className = ''
}) => {
  return (
    <CommandGroup heading={heading} className={className}>
      {children}
    </CommandGroup>
  );
};
